import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableRowProps,
  Typography,
  styled
} from "@mui/material";
import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";

import { UserContext } from "../../../../Context/UserContext";
import { Customer, EnhancedCustomer } from "../../../../Models/Customer";
import { Opportunity } from "../../../../Models/Opportunity";
import { DateTime } from "../../../../Types/DateTime";

interface Props {
  customers?: Customer[];
  disabled?: boolean;
  opportunities?: Record<string, Opportunity[]>;
  selectedCustomerId?: string;
  selectedOpportunityId?: string;
  setPreviewCustomer: Dispatch<SetStateAction<EnhancedCustomer | undefined>>;
  setOpportunity?: () => void;
}
const CustomerTable: React.FC<Props> = ({
  customers,
  disabled = false,
  opportunities,
  selectedCustomerId,
  selectedOpportunityId,
  setPreviewCustomer,
  setOpportunity
}: Props) => {
  const { centers } = useContext(UserContext);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  // ON CUSTOMERS CHANGING
  useEffect(
    () =>
      // clear the preview customer if it doesn't exist in the new dataset
      setPreviewCustomer(customer => {
        if (!customer) return customer;
        const exists = customers?.some(c => c.id === customer.id) || false;
        if (!exists && setOpportunity) setOpportunity();
        return exists ? customer : undefined;
      }),
    [customers, setOpportunity, setPreviewCustomer]
  );

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  }, []);
  const handleReset = () => {
    setPreviewCustomer(undefined);
    setOpportunity && setOpportunity();
  };
  const handleClickOpportunity = setOpportunity
    ? (opportunity: Opportunity) => {
        setOpportunity();
        setPreviewCustomer(pc => (pc && { ...pc, opportunity }) || undefined);
      }
    : undefined;
  const handleClickCustomer = (customer: Customer) => {
    setOpportunity && setOpportunity();
    const center = customer.centerId ? centers.find(c => c.id === customer.centerId) : undefined;
    setPreviewCustomer(
      new EnhancedCustomer(opportunities ? { ...customer, opportunity: null, center } : { ...customer, center })
    );
  };
  const startIndex = page * rowsPerPage;
  return (
    <Paper sx={{ overflow: "hidden" }}>
      <Table
        stickyHeader
        size="small"
        sx={disabled ? { opacity: 0.5, userSelect: "none", pointerEvents: "none" } : undefined}
      >
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell align="right">Mobile Phone</TableCell>
            <TableCell align="right">Home Phone</TableCell>
            <TableCell align="right">Work Phone</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customers &&
            (customers.length ? (
              customers
                .slice(startIndex, startIndex + rowsPerPage)
                .map((customer, index) => (
                  <CustomerTableRow
                    hover
                    tabIndex={-1}
                    isEven={selectedCustomerId !== customer.id && Boolean(index % 2)}
                    key={index}
                    onClick={() => !disabled && handleClickCustomer(customer)}
                    handleClickOpportunity={handleClickOpportunity}
                    customer={customer}
                    opportunities={opportunities}
                    selected={selectedCustomerId === customer.id}
                    selectedOpportunityId={selectedOpportunityId}
                  />
                ))
            ) : (
              <TableRow sx={{ cursor: "default" }}>
                <TableCell colSpan={5}>No Search Results</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Button
            variant="text"
            onClick={handleReset}
            color="secondary"
            disabled={!selectedCustomerId || disabled}
            sx={{ ml: 1 }}
          >
            Reset Selection
          </Button>
        </Grid>
        <Grid item>
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10]}
            count={customers?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={disabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

interface RowProps extends TableRowProps {
  customer: Customer;
  opportunities?: Record<string, Opportunity[]>;
  selected: boolean;
  selectedOpportunityId?: string;
  onClick: () => void;
  handleClickOpportunity?: (opportunity: Opportunity) => void;
}
const UnstyledCustomerTableRow: React.FC<RowProps> = ({
  customer,
  opportunities,
  selected,
  selectedOpportunityId,
  handleClickOpportunity,
  ...rest
}: RowProps) => (
  <>
    <TableRow selected={selected} {...rest}>
      <TableCell>{`${customer.firstName} ${customer.lastName}`}</TableCell>
      <TableCell>{customer.email || ""}</TableCell>
      <TableCell align="right">{customer.phoneNumber}</TableCell>
      <TableCell align="right">{customer.homePhoneNumber}</TableCell>
      <TableCell align="right">{customer.workPhoneNumber}</TableCell>
    </TableRow>
    {handleClickOpportunity && (
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={selected} timeout="auto" unmountOnExit>
            <Box margin={1}>
              {!!customer.id &&
                !!opportunities &&
                (!opportunities[customer.id] ? (
                  <Grid container item xs justifyContent="center">
                    <CircularProgress />
                  </Grid>
                ) : opportunities[customer.id].length ? (
                  <>
                    <Typography variant="body2" gutterBottom component="div">
                      <strong>Opportunities</strong>
                    </Typography>
                    <Table size="small" padding="none">
                      <TableHead>
                        <TableRow>
                          <TableCell>Created Date</TableCell>
                          <TableCell>Marketing Campaign</TableCell>
                          <TableCell>Business Unit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {opportunities[customer.id].map((oppo, index) => (
                          <TableRow
                            hover
                            onClick={() => handleClickOpportunity(oppo)}
                            selected={oppo.id === selectedOpportunityId}
                            key={index}
                          >
                            <TableCell>
                              {oppo.externalCreatedAtUtc
                                ? DateTime.fromISO(oppo.externalCreatedAtUtc).toLocaleString(DateTime.DATETIME_MED)
                                : ""}
                            </TableCell>
                            <TableCell>{oppo.campaign.name}</TableCell>
                            <TableCell>{oppo.campaign.businessUnit.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <Typography variant="body2" gutterBottom component="div">
                    <strong>No Opportunities</strong>
                  </Typography>
                ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    )}
  </>
);

interface StyledRowProps extends RowProps {
  isEven: boolean;
}

const CustomerTableRow = styled(UnstyledCustomerTableRow, {
  shouldForwardProp: prop => prop !== "isEven"
})<StyledRowProps>(({ isEven, opportunities, theme }) => ({
  "&": {
    backgroundColor: isEven ? theme.palette.divider : undefined,
    borderBottom: opportunities ? "unset" : undefined
  }
}));

export default CustomerTable;
